//@ts-nocheck
//@ts-nocheck
import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonProps,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { ITuteriaSubject } from "../stores";

const TestPage: React.FC<{
  heading: string;
  description: string;
  testableSubjects: string[];
  subjectInfo: ITuteriaSubject;
  onSubjectSelect: (value: string[]) => void;
  toSubjectPage: () => void;
  buttonProps: ButtonProps;
  isReview: boolean;
  onRefresh: (subjects: any) => Promise<any>;
  isLiteTutor?: boolean;
  selectedQuizzes?: string[];
}> = ({
  heading,
  description,
  testableSubjects,
  onSubjectSelect,
  toSubjectPage,
  subjectInfo,
  isLiteTutor = false,
  buttonProps,
  isReview = false,
  onRefresh,
  selectedQuizzes = [],
}) => {
  return (
    <Stack spacing={[6, 8]}>
      <Button
        alignSelf="start"
        onClick={toSubjectPage}
        leftIcon={<ArrowBackIcon />}
        variant="link"
        colorScheme="blue"
      >
        Back
      </Button>

      <Stack spacing={3} alignItems="flex-start">
        <Text fontSize="2xl" fontWeight="bold">
          {heading} test
        </Text>
        <Text color="gray.600">
          {isLiteTutor
            ? `This subject contains a mix of English Language, Mathematics, Basic Science, Verbal, and Quantitative reasoning. In order to teach these subjects, you need to pass an online competency test. Please carefully read the instructions below.`
            : description}
        </Text>
      </Stack>

      <Stack
        spacing={3}
        p={6}
        rounded="md"
        borderColor="neutral.500"
        borderWidth="2xs"
      >
        <Text fontWeight="bold" fontSize="lg">
          Test instructions
        </Text>
        {/* <Text>
          The test duration is {subjectInfo.duration} minutes, and comprises of{" "}
          {subjectInfo.questionCount} multiple choice questions. The pass mark
          is {subjectInfo.pass_mark}% and you have 2 attempts.
        </Text> */}
        <Text>
          The test duration is {subjectInfo.duration} minutes,{" "}
          {isLiteTutor ? `comprising 30` : `and comprises of 10 - 15`} multiple
          choice questions. The pass mark is {subjectInfo.pass_mark}% and you
          have 2 attempts.
        </Text>
        <Text>
          Please note that once you begin the test, it can't be stopped or
          canceled until it's submitted.
        </Text>
        <Text>
          Ensure you have enough power and good connectivity on your device
          before you begin. Also, please be prepared with a paper, pen and
          calculator for Math-related tests. You may need it.
        </Text>
        <Text>
          After you pass the test, you can proceed to add this subject, set your
          prices and start receiving clients. All the best!
        </Text>
        <Box pt={5}>
          <Button
            colorScheme="blue"
            isFullWidth
            size="lg"
            loadingText="Fetching questions"
            {...(buttonProps as any)}
          >
            Begin test
          </Button>
        </Box>
      </Stack>

      {isReview && (
        <Box width="100%">
          <CheckboxGroup colorScheme="green" onChange={onSubjectSelect}>
            <Flex flexDir="column" p={2}>
              {testableSubjects.map((subject, index) => {
                return (
                  <Checkbox
                    key={index}
                    m={2}
                    borderColor="neutral.500"
                    value={subject}
                  >
                    {subject}
                  </Checkbox>
                );
              })}
            </Flex>
          </CheckboxGroup>

          <Button
            colorScheme="green"
            variant="outline"
            {...(buttonProps as any)}
            isDisabled={selectedQuizzes.length != 1}
            loadingText="Refreshing..."
            onClick={onRefresh}
          >
            Refresh
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export const QuizSelectionView: React.FC<{
  toSubjectPage: () => void;
  testSubject: string;
  testableSubjects: any[];
  canTakeQuiz?: boolean;
  isLiteTutor?: boolean;
  subjectInfo: ITuteriaSubject;
  loading: boolean;
  generateQuiz: (subjects: any[]) => Promise<any>;
  isReview: boolean;
  onRefresh: (subjects: any) => Promise<any>;
}> = observer(
  ({
    toSubjectPage,
    testSubject,
    testableSubjects,
    generateQuiz,
    subjectInfo,
    canTakeQuiz = true,
    isLiteTutor = false,
    loading,
    isReview = false,
    onRefresh = () => {},
  }) => {
    const [selectedQuizzesToTake, updateQuizzesToTake] = React.useState([]);
    // const [loading, setLoading] = React.useState(false);

    async function onSubmit() {
      // try {
      //   setLoading(true);
      await generateQuiz(selectedQuizzesToTake);
      // } catch (error) {
      //   console.log(error);
      //   setLoading(false);
      // }
    }

    async function refresh() {
      await onRefresh(selectedQuizzesToTake);
    }

    React.useEffect(() => {
      if (!isReview) {
        updateQuizzesToTake(testableSubjects);
      }
    }, [isReview, testableSubjects]);

    return canTakeQuiz ? (
      <TestPage
        heading={testSubject}
        subjectInfo={subjectInfo}
        description={`In order to teach this subject, you need to pass an online competency test. Please carefully read the instructions below.`}
        testableSubjects={testableSubjects}
        isLiteTutor={isLiteTutor}
        onSubjectSelect={(subjects) => {
          updateQuizzesToTake(subjects);
        }}
        toSubjectPage={toSubjectPage}
        buttonProps={{
          loadingText: "Starting test",
          isLoading: loading,
          isDisabled:
            testableSubjects.length > 0
              ? selectedQuizzesToTake.length === 0
              : false,
          onClick: onSubmit,
        }}
        isReview={isReview}
        onRefresh={refresh}
        selectedQuizzes={selectedQuizzesToTake}
      />
    ) : (
      <Stack spacing={3}>
        <Text fontSize="2xl" fontWeight="bold">
          Sorry, you can't take this test at the moment.
        </Text>
        <Text>
          You may already have taken this test, or there was an issue fetching
          the right test questions for you. Please go back to the subjects list
          and click on the subject test from there.
        </Text>

        <Box pt={5}>
          <Button
            colorScheme="blue"
            isFullWidth
            leftIcon={<ArrowBackIcon />}
            onClick={toSubjectPage}
            size="lg"
          >
            Back to subjects
          </Button>
        </Box>
      </Stack>
    );
  }
);

export default QuizSelectionView;
